import { useCallback } from 'react'
import { useSession } from './session-context'
import { useTimer } from './timer/timer-context'

/** Which UI affordance asked for the code swap. */
export type ApplyCodeSource =
  | 'attempt-restore'
  | 'attempt-transfer'
  | 'toolbar-reset'
  | 'toolbar-restore-last'

/**
 * Outcome of an apply. Named and annotated rather than inferred from `as const`
 * literals: callers discriminate on `ok` and stringify `reason`, so a member
 * whose `ok` or `reason` silently widened to `boolean`/`string` would defeat
 * both without erroring at the `return` that caused it.
 *
 * The failure reasons include every `DraftSaveResult` failure verbatim, so
 * adding one there fails to compile here until it is handled.
 */
export type ApplyCodeResult =
  | { ok: true; sessionId: string; source: ApplyCodeSource }
  | {
      ok: false
      reason: 'session-create-failed' | 'no-session' | 'draft-save-failed'
    }

export function useApplyCodeFromAction() {
  const session = useSession()
  const timer = useTimer()

  return useCallback(
    async (input: {
      code: string
      language: string
      source: ApplyCodeSource
    }): Promise<ApplyCodeResult> => {
      // 1. Ensure a session exists
      let sessionId = session.session?.id ?? null
      if (!sessionId) {
        const created = await session.createSession()
        sessionId = created?.id ?? null
        if (!sessionId) {
          return { ok: false, reason: 'session-create-failed' }
        }
      }

      // 2. Save the new code as the latest sessionDraft (immediate, bypasses debounce)
      // A failed persist leaves the editor showing the pre-restore code, so bail
      // here rather than reporting success — every caller gates its "done" UI
      // (close dialog, notifyRestoreCompleted, switch to Code) on `ok`. Forward
      // the reason rather than flattening it: attempts-pane logs it, and
      // 'no-session' vs 'draft-save-failed' point at different root causes.
      const saved = await session.saveDraftImmediate(input.language, input.code)
      if (!saved.ok) {
        return { ok: false, reason: saved.reason }
      }

      // 3. Stopwatch logic — idle/paused → start; running → no-op
      if (
        timer.stopwatch.status === 'idle' ||
        timer.stopwatch.status === 'paused'
      ) {
        timer.startStopwatch()
      }
      // running: leave as-is

      return {
        ok: true,
        sessionId,
        source: input.source,
      }
    },
    [session, timer],
  )
}
